import {
    Autocomplete, Box, InputAdornment, Table, TableBody, TableCell,
    TableContainer, TableHead, TablePagination, TableRow,
    TextField, Typography, Button,
} from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import SearchIcon from '@mui/icons-material/Search'
import DownloadIcon from '@mui/icons-material/Download'
import GavelIcon from '@mui/icons-material/Gavel'
// import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
// import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
// import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import { ApiGetAuditLogs, ApiDownloadAuditLogsExcel } from '../../apiService/Core/Apicall'
import PopupDialog from '../../Components/Popups/ConfirmDialog'
import { useDateRange } from '../../hooks/useDateRange'
import DateRangeFilter from '../../Components/Common/DateRangeFilter'
import { usePersistedState } from '../../hooks/userPersistedState'

const fmtDate = (d: string) =>
    new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })
const fmtTime = (d: string) =>
    new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })

const fmtValue = (val: any) => {
    if (val === null || val === undefined) return '—'
    if (typeof val !== 'object') return String(val)

    if (val.feeType !== undefined && val.value !== undefined) {
        return `${val.feeType}: ${val.value}%`
    }

    if (val.scope !== undefined) {
        const parts = [`Scope: ${val.scope}`]
        if (val.feeType !== undefined) parts.push(`${val.feeType}: ${val.fee}${val.feeType === 'percentage' ? '%' : ''}`)
        if (val.isActive !== undefined) parts.push(val.isActive ? 'Active' : 'Inactive')
        return parts.join(' | ')
    }

    const entries = Object.entries(val)
    if (entries.length === 0) return '—'
    return entries.map(([k, v]) => `${k}: ${v}`).join(', ')
}

// const statusMap: Record<string, { color: string; bg: string; border: string }> = {
//     approved: { color: '#10b981', bg: '#10b98118', border: '#10b98150' },
//     pending: { color: '#3b82f6', bg: '#3b82f618', border: '#3b82f650' },
//     rejected: { color: '#ef4444', bg: '#ef444418', border: '#ef444450' },
//     auto: { color: '#9ca3af', bg: '#9ca3af18', border: '#9ca3af50' },
// }

// const StatusPill: React.FC<{ status: string }> = ({ status }) => {
//     const s = statusMap[status] || statusMap.auto
//     return (
//         <Box sx={{
//             display: 'inline-flex', alignItems: 'center', gap: 0.6,
//             px: 1.5, py: 0.4, borderRadius: '6px', fontSize: '11px', fontWeight: 700,
//             color: s.color, backgroundColor: s.bg, border: `1px solid ${s.border}`,
//             letterSpacing: '0.03em', whiteSpace: 'nowrap', textTransform: 'uppercase',
//         }}>
//             <Box sx={{ width: 5, height: 5, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
//             {status}
//         </Box>
//     )
// }

// const StatCard: React.FC<{ label: string; value: string; sub?: string; color: string; icon: React.ReactNode }> = ({ label, value, sub, color, icon }) => (
//     <Box sx={{
//         background: 'linear-gradient(135deg, #2a2a2a 0%, #222 100%)',
//         border: '1px solid #333', borderRadius: '14px', p: 2.5,
//         position: 'relative', overflow: 'hidden', transition: 'border-color 0.25s',
//         '&:hover': { borderColor: color + '60' },
//         '&::before': {
//             content: '""', position: 'absolute', top: 0, left: 0, right: 0,
//             height: '2px', background: `linear-gradient(90deg, ${color}00, ${color}, ${color}00)`,
//         },
//     }}>
//         <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
//             <Typography fontSize={10} color="#6f6f6f" textTransform="uppercase" letterSpacing="0.1em">{label}</Typography>
//             <Box sx={{ color, opacity: 0.7 }}>{icon}</Box>
//         </Box>
//         <Typography fontSize={20} fontWeight={800} color={color} sx={{ letterSpacing: '-0.02em' }}>{value}</Typography>
//         {sub && <Typography fontSize={10} color="#5a5a5a" mt={0.5}>{sub}</Typography>}
//     </Box>
// )

const thStyle = {
    backgroundColor: '#241815', color: '#ff6a3d', fontSize: 11, fontWeight: 700,
    letterSpacing: '0.08em', textTransform: 'uppercase' as const,
    whiteSpace: 'nowrap' as const, py: 1.5, borderBottom: '1px solid #2a2a2a !important',
    position: 'sticky' as const, top: 0, zIndex: 2,
}
const tdStyle = { color: '#c0c0c0', py: 1.4, fontSize: 13, whiteSpace: 'nowrap' as const }
const trHover = { transition: 'background 0.15s', '&:hover': { background: '#ff6a3d08' } }
const DEFAULT_PAGE_LIMIT = 10

const searchFieldStyle = {
    width: 300, background: 'transparent', borderRadius: '7px',
    border: '1px solid #898989', transition: 'border-color 0.3s',
    '&:hover': { border: '1px solid #ff6a3d' },
    '&:focus-within': { border: '1px solid #ff6a3d' },
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '& input': { color: '#fff', fontSize: 13 },
    '& input::placeholder': { color: '#555', opacity: 1 },
}

const AuditLogs: React.FC = () => {
    const {
        range, customStart, setCustomStart, customEnd, setCustomEnd, handleRangeChange, buildQuery,
    } = useDateRange('auditLogs', 'all')

    const [logs, setLogs] = useState<any[]>([])
    const [total, setTotal] = useState(0)
    // const [stats, setStats] = useState<any>(null)
    const [page, setPage] = usePersistedState('auditLogs:page', 0)
    const [dataPage, setDataPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = usePersistedState('auditLogs:rowsPerPage', DEFAULT_PAGE_LIMIT)
    const [search, setSearch] = usePersistedState('auditLogs:search', '')
    const [debouncedSearch, setDebouncedSearch] = useState('')
    const [action, setAction] = usePersistedState('auditLogs:action', '')
    const [approvalStatus,] = usePersistedState('auditLogs:approvalStatus', '')
    const [errorPopup, setErrorPopup] = useState({ open: false, message: '' })
    const [downloading, setDownloading] = useState(false)
    const requestIdRef = React.useRef(0)

    const ACTION_OPTIONS = [
    'POST_PROCESS_WITHDRAWAL_APPROVAL', 'POST_PROCESS_WITHDRAWAL_REJECTION',
    'POST_AEROPAY_WITHDRAW_APPROVE', 'POST_AEROPAY_WITHDRAW_REJECT',
    'POST_ACCEPT_REFERRAL_DEPOSIT', 'POST_REJECT_REFERRAL_DEPOSIT',
    'UPDATE_GLOBAL_FEES',
    'CREATE_COUNTRY_FEE', 'CREATE_STATE_FEE',
    'UPDATE_COUNTRY_FEE', 'UPDATE_STATE_FEE',
    'UPDATE_COUNTRY_FEE_STATUS', 'UPDATE_STATE_FEE_STATUS',
    'PATCH_UPDATE_C2C_TRADABLE',
    'POST_SPINS_ADJUST',
    'DELETE_ADMIN_USERS_DELETE_INCOMPLETE_ONBOARDING',
    'PUSH_GROUP_CREATED', 'PUSH_GROUP_MODIFIED', 'PUSH_GROUP_DELETED',
    'PUSH_CAMPAIGN_SEND_REQUESTED', 'PUSH_CAMPAIGN_SEND_REPEATED', 'PUSH_CAMPAIGN_RETRY_REQUESTED',
    'PUSH_CAMPAIGN_CANCELLED', 'PUSH_TEST_SENT',
    'PUSH_CAMPAIGN_STARTED', 'PUSH_CAMPAIGN_COMPLETED', 'PUSH_CAMPAIGN_FAILED',
]
    // const APPROVAL_STATUS_OPTIONS = ['approved', 'pending', 'rejected', 'auto']

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 500)
        return () => clearTimeout(timer)
    }, [search])

    const prevFiltersRef = React.useRef({ debouncedSearch, range, customStart, customEnd, action, approvalStatus })
    useEffect(() => {
        const prev = prevFiltersRef.current
        const changed =
            prev.debouncedSearch !== debouncedSearch ||
            prev.range !== range ||
            (prev.customStart?.valueOf() !== customStart?.valueOf()) ||
            (prev.customEnd?.valueOf() !== customEnd?.valueOf()) ||
            prev.action !== action ||
            prev.approvalStatus !== approvalStatus
        if (changed) {
            setPage(0)
            setDataPage(0)
            setLogs([])
        }
        prevFiltersRef.current = { debouncedSearch, range, customStart, customEnd, action, approvalStatus }
    }, [debouncedSearch, range, customStart, customEnd, action, approvalStatus, setPage])

    const buildFilterQuery = useCallback(() => {
        const dateQuery = buildQuery()
        if (dateQuery === null) return null
        return `?search=${encodeURIComponent(debouncedSearch)}&page=${page + 1}&limit=${rowsPerPage}${dateQuery ? `&${dateQuery}` : ''}${action ? `&action=${encodeURIComponent(action)}` : ''}${approvalStatus ? `&approvalStatus=${encodeURIComponent(approvalStatus)}` : ''}`
    }, [debouncedSearch, page, rowsPerPage, buildQuery, action, approvalStatus])

    const fetchData = useCallback(async () => {
        const requestId = ++requestIdRef.current
        try {
            const query = buildFilterQuery()
            if (query === null) return
            const res = await ApiGetAuditLogs(query)
            if (requestId !== requestIdRef.current) return
            if (res?.status) {
                const d = res.data
                setLogs(d.logs || [])
                setTotal(d.pagination?.total || 0)
                setDataPage(page)
                // setStats(d.stats)
            }
        } catch (e: any) {
            if (requestId !== requestIdRef.current) return
            setErrorPopup({
                open: true,
                message: e?.message || e?.response?.data?.message || 'Failed to fetch audit logs',
            })
        }
    }, [buildFilterQuery, page])

    useEffect(() => { fetchData() }, [fetchData])

    const handleDownload = async () => {
        setDownloading(true)
        try {
            const dateQuery = buildQuery()
            const query = `?search=${encodeURIComponent(debouncedSearch)}${dateQuery ? `&${dateQuery}` : ''}${action ? `&action=${encodeURIComponent(action)}` : ''}${approvalStatus ? `&approvalStatus=${encodeURIComponent(approvalStatus)}` : ''}`
            const blob = await ApiDownloadAuditLogsExcel(query)
            const url = window.URL.createObjectURL(new Blob([blob]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', `audit-logs-${Date.now()}.xlsx`)
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)
        } catch (e: any) {
            setErrorPopup({
                open: true,
                message: e?.message || e?.response?.data?.message || 'Failed to download audit logs',
            })
        } finally {
            setDownloading(false)
        }
    }

    return (
        <div className='common-layout flex-column'>
            <Box className="border-10 p-2 px-4 mixed-orange mb-4 both-circle d-flex align-items-center justify-content-between">
                <Box className="d-flex gap-3 align-items-center">
                    <Typography className='fs-24 text-white fw-medium' sx={{ lineHeight: 1.1 }}>
                        Audit Logs
                    </Typography>
                </Box>
            </Box>

            <DateRangeFilter
                range={range}
                onRangeChange={handleRangeChange}
                customStart={customStart}
                onCustomStartChange={setCustomStart}
                customEnd={customEnd}
                onCustomEndChange={setCustomEnd}
            />

            {/* <Grid container spacing={2} mb={3} mt={2}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <StatCard label="Approved Actions" value={`${stats?.approvedCount ?? 0}`}
                        sub="Total approved entries" color="#10b981" icon={<CheckCircleOutlineIcon fontSize="small" />} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <StatCard label="Pending Approval" value={`${stats?.pendingCount ?? 0}`}
                        sub="Awaiting review" color="#3b82f6" icon={<HourglassEmptyIcon fontSize="small" />} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <StatCard label="Rejected Actions" value={`${stats?.rejectedCount ?? 0}`}
                        sub="Total rejected entries" color="#ef4444" icon={<CancelOutlinedIcon fontSize="small" />} />
                </Grid>
            </Grid> */}

            <Box className="bg-dark-color border-10 p-0 mt-2" sx={{ overflow: 'hidden' }}>
                <Box sx={{ p: '16px 20px', borderBottom: '1px solid #1e1e1e', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <GavelIcon sx={{ color: '#ff6a3d', fontSize: 20 }} />
                        <Typography fontSize={14} fontWeight={700} color="#e0e0e0">Activity Log</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <TextField
                            placeholder="Search admin…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            size="small"
                            sx={{
                                width: 280, background: 'transparent', borderRadius: '7px',
                                border: '1px solid #898989', transition: 'border-color 0.3s',
                                '&:hover': { border: '1px solid #ff6a3d' },
                                '&:focus-within': { border: '1px solid #ff6a3d' },
                                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                '& input': { color: '#fff', fontSize: 13 },
                                '& input::placeholder': { color: '#555', opacity: 1 },
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon className='color-primary' />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Autocomplete
                            size="small"
                            value={action}

                            disableClearable={!action}
                            onChange={(_, newValue) => setAction(newValue || '')}
                            options={ACTION_OPTIONS}
                            renderInput={(params) => (
                                <TextField {...params} placeholder="Action Type" sx={searchFieldStyle}
                                    InputProps={{ ...params.InputProps, sx: { color: '#fff', '& .MuiAutocomplete-endAdornment .MuiSvgIcon-root': { color: '#fff' } } }}
                                />
                            )}
                            PaperComponent={({ children }) => (
                                <Box className="bg-dark-color" sx={{ fontSize: '0.530rem', fontWeight: '800', color: '#fff', '& .MuiAutocomplete-option': { '&:hover': { bgcolor: '#241815' }, "&[aria-selected='true']": { bgcolor: '#241815' } } }}>
                                    {children}
                                </Box>
                            )}
                        />
                        <Button
                            variant="contained"
                            startIcon={<DownloadIcon />}
                            onClick={handleDownload}
                            disabled={downloading}
                            sx={{
                                fontSize: 11, fontWeight: 700, borderRadius: '7px',
                                color: '#ff6a3d', border: '1px solid #ff6a3d40',
                                background: '#ff6a3d10', px: 1.5, py: 1.0, minWidth: 80,
                                textTransform: 'none',
                                '&:hover': { background: '#ff6a3d20', borderColor: '#ff6a3d' },
                            }}
                        >
                            {downloading ? 'Preparing…' : 'Export to Excel'}
                        </Button>
                        {/* <Autocomplete
                            size="small"
                            value={approvalStatus}
                            disableClearable={!approvalStatus}
                            onChange={(_, newValue) => setApprovalStatus(newValue || '')}
                            options={APPROVAL_STATUS_OPTIONS}
                            renderInput={(params) => (
                                <TextField {...params} placeholder="Approval Status" sx={searchFieldStyle}
                                    InputProps={{ ...params.InputProps, sx: { color: '#fff', '& .MuiAutocomplete-endAdornment .MuiSvgIcon-root': { color: '#fff' } } }}
                                />
                            )}
                            PaperComponent={({ children }) => (
                                <Box className="bg-dark-color" sx={{ color: '#fff', '& .MuiAutocomplete-option': { '&:hover': { bgcolor: '#241815' }, "&[aria-selected='true']": { bgcolor: '#241815' } } }}>
                                    {children}
                                </Box>
                            )}
                        /> */}
                    </Box>
                </Box>

                <TableContainer sx={{ maxHeight: '55vh' }}>
                    <Table stickyHeader sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid #1e1e1e' } }}>
                        <TableHead>
                            <TableRow>
                                {['#', 'Date/Time', 'Admin User', 'Action', 'Customer/Account', 'Previous Value', 'New Value'].map(h => (
                                    <TableCell key={h} sx={thStyle}>{h}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {logs.length > 0 ? logs.map((log: any, i: number) => (
                                <TableRow key={log._id} sx={trHover}>
                                    <TableCell sx={tdStyle}>{dataPage * rowsPerPage + i + 1}</TableCell>
                                    <TableCell sx={tdStyle}>
                                        <Typography fontSize={13} color="#e0e0e0">{fmtDate(log.timestamp)}</Typography>
                                        <Typography fontSize={10} color="#5a5a5a">{fmtTime(log.timestamp)}</Typography>
                                    </TableCell>
                                    <TableCell sx={tdStyle}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                            <PersonOutlineIcon sx={{ fontSize: 16, color: '#7a7a7a' }} />
                                            <Box>
                                                <Typography fontSize={13} fontWeight={600} color="#e0e0e0">{log.adminUser?.name || '—'}</Typography>
                                                <Typography fontSize={10} color="#5a5a5a">{log.adminUser?.email || '—'}</Typography>
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={tdStyle}>
                                        <Box sx={{ display: 'inline-block', px: 1.2, py: 0.3, borderRadius: '5px', fontSize: 10, fontWeight: 700, color: '#f2674a', background: '#f2674a15', border: '1px solid #f2674a30' }}>
                                            {log.action}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={tdStyle}>
                                        <Typography fontSize={12} color="#c0c0c0">
                                            {log.customer?.identifier || log.customer?.userId || '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ ...tdStyle, maxWidth: 200, whiteSpace: 'normal' }}>
                                        <Typography fontSize={12} color="#7a7a7a">
                                            {fmtValue(log.previousValue)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ ...tdStyle, maxWidth: 200, whiteSpace: 'normal' }}>
                                        <Typography fontSize={12} color="#10b981" fontWeight={600}>
                                            {fmtValue(log.newValue)}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 8, background: '#1a1a1a', border: 'none' }}>
                                        <GavelIcon sx={{ color: '#2f2f2f', fontSize: 40, mb: 1 }} />
                                        <Typography color="#4a4a4a" fontSize={13}>No audit logs found</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div" count={total}
                    page={page} onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); setDataPage(0); setLogs([]) }}
                    rowsPerPageOptions={[5, 10, 25, 50]} showFirstButton showLastButton
                    sx={{
                        color: '#6a6a6a', borderTop: '1px solid #252525',
                        '& .MuiTablePagination-toolbar': { justifyContent: 'center' },
                        '& .MuiTablePagination-selectLabel': { color: '#6a6a6a', margin: 0 },
                        '& .MuiTablePagination-displayedRows': { color: '#6a6a6a', margin: 0 },
                        '& .MuiSelect-select': { color: '#6a6a6a' },
                        '& .MuiSelect-icon': { color: '#6a6a6a' },
                        '& .MuiIconButton-root': { color: '#6a6a6a' },
                        '& .MuiIconButton-root.Mui-disabled': { color: '#333' },
                    }}
                />
            </Box>

            <PopupDialog
                open={errorPopup.open}
                onClose={() => setErrorPopup({ open: false, message: '' })}
                onConfirm={() => setErrorPopup({ open: false, message: '' })}
                variant="error"
                title="Something Went Wrong"
                message={errorPopup.message}
                confirmText="Try Again"
                cancelText="Dismiss"
            />
        </div>
    )
}

export default AuditLogs